import { 
  useCallback, useEffect, useSyncExternalStore,
} from 'react';

type Key = string;
type Value = any;
type Callback = ((e: Value) => Value) | Value;

interface IUseLocalStorageParams {
  key: Key;
  initialValue?: Value;
}

type IUseLocalStorageResponse = [Value, (v: Callback) => void];

const useLocalStorage = ({
  key, 
  initialValue,
}: IUseLocalStorageParams): IUseLocalStorageResponse => {
  const getSnapshot = () => getLocalStorageItem(key);

  const store = useSyncExternalStore(
    useLocalStorageSubscribe,
    getSnapshot,
    getLocalStorageServerSnapshot,
  );

  const setState = useCallback(
    (v: Callback) => {
      try {
        const oldValue = store ? JSON.parse(store) : store;
        const nextState = typeof v === 'function' ? v(oldValue) : v;

        if (nextState === undefined || nextState === null) {
          removeLocalStorageItem(key);
        } else {
          setLocalStorageItem(key, nextState);
        }
      } catch (e) {
        console.warn(e);
      }
    },
    [key, store],
  );

  useEffect(() => {
    if (
      getLocalStorageItem(key) === null 
      && typeof initialValue !== 'undefined'
    ) {
      setLocalStorageItem(key, initialValue);
    }
  }, [key, initialValue]);

  return [store ? JSON.parse(store) : initialValue, setState];
};

const getLocalStorageItem = (key: Key) => window.localStorage.getItem(key);

const useLocalStorageSubscribe = (callback: any) => {
  window.addEventListener('storage', callback);
  return () => window.removeEventListener('storage', callback);
};

const getLocalStorageServerSnapshot = () => {
  throw Error('useLocalStorage is a client-only hook');
};

function dispatchStorageEvent(key: Key, newValue: Value) {
  window.dispatchEvent(new StorageEvent('storage', { key, newValue }));
}

const setLocalStorageItem = (key: Key, value: Value) => {
  const stringifiedValue = JSON.stringify(value);
  window.localStorage.setItem(key, stringifiedValue);
  dispatchStorageEvent(key, stringifiedValue);
};

const removeLocalStorageItem = (key: Key) => {
  window.localStorage.removeItem(key);
  dispatchStorageEvent(key, null);
};

export default useLocalStorage;
